/**
 * @fileoverview JobRateSettingsDialog — choose how one job is charged
 * (MOR-1018, MOR-1038).
 *
 * Two independent choices: the rate card (Standard, or a multiplier card) and
 * an optional freeze ("charge at the rates as at this date", which is how a
 * matter stays on last year's rates).
 *
 * Either one re-values the job's unapproved time, so the dialog always shows
 * what the change does — how many entries, the dollar movement, and anyone it
 * would leave without a rate — before the admin applies it. Approved time is
 * snapshotted and never moves; the dialog says that too. Admin/owner only.
 */

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";

import { extractErrorMessage } from "@/utils/errors";
import { Button, Label } from "@/ui/primitives";
import { Switch } from "@/ui/primitives/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import { formatAud } from "./format";
import { describeMultiplier, formatIsoDate } from "./rateCards.helpers";

const STANDARD = "standard";
/** Changes listed before "Show all": enough for context, short enough to scan. */
const RECENT_HISTORY = 5;

export interface JobRateTermsView {
  rateCardId?: Id<"rateCards">;
  rateCardName?: string;
  multiplier?: number;
  cardArchived: boolean;
  ratesAsAt?: string;
  history: { timestamp: number; actor: string; reason: string }[];
}

export function JobRateSettingsDialog({
  open,
  orgId,
  jobId,
  current,
  onOpenChange,
}: {
  open: boolean;
  orgId: Id<"organizations">;
  jobId: Id<"jobs">;
  current: JobRateTermsView;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: cards } = useQuery({
    ...convexQuery(api.timesheets.rateCards.listByOrg, { orgId }),
    enabled: open,
  });
  const setForJob = useMutation(api.timesheets.jobRates.setForJob);

  const [cardId, setCardId] = useState<string>(STANDARD);
  const [frozen, setFrozen] = useState(false);
  const [ratesAsAt, setRatesAsAt] = useState("");
  const [saving, setSaving] = useState(false);
  const [showAllHistory, setShowAllHistory] = useState(false);

  useEffect(() => {
    if (!open) return;
    setShowAllHistory(false);
    setCardId(current.rateCardId ?? STANDARD);
    setFrozen(current.ratesAsAt !== undefined);
    setRatesAsAt(current.ratesAsAt ?? "");
  }, [open, current.rateCardId, current.ratesAsAt]);

  const proposedCardId =
    cardId === STANDARD ? null : (cardId as Id<"rateCards">);
  const proposedAsAt = frozen && ratesAsAt ? ratesAsAt : null;
  const freezeIncomplete = frozen && !ratesAsAt;
  const changed =
    proposedCardId !== (current.rateCardId ?? null) ||
    proposedAsAt !== (current.ratesAsAt ?? null);

  // Keyed on the proposed terms, so `preview` is only ever the preview OF
  // those terms: picking something else drops back to undefined until the
  // new figures land.
  const {
    data: preview,
    isError: previewFailed,
    refetch: retryPreview,
  } = useQuery({
    ...convexQuery(api.timesheets.jobRates.previewChange, {
      jobId,
      rateCardId: proposedCardId,
      ratesAsAt: proposedAsAt,
    }),
    enabled: open && changed && !freezeIncomplete,
  });
  // Applying re-values money, so it waits for the admin to have been shown
  // what it does (QA-32): no preview for these exact terms, no Apply.
  const canApply =
    changed && !freezeIncomplete && preview !== undefined && !saving;

  // A job may sit on a card that has since been archived: keep it selectable
  // so opening the dialog does not silently move the job off it.
  const cardOptions = [
    ...(cards ?? []),
    ...(current.rateCardId &&
    current.cardArchived &&
    !(cards ?? []).some((c) => c._id === current.rateCardId)
      ? [
          {
            _id: current.rateCardId,
            name: `${current.rateCardName ?? "Archived card"} (archived)`,
            multiplier: current.multiplier ?? 1,
          },
        ]
      : []),
  ];

  const handleApply = async () => {
    if (!canApply) return;
    setSaving(true);
    try {
      await setForJob({
        jobId,
        rateCardId: proposedCardId,
        ratesAsAt: proposedAsAt,
      });
      toast.success("Job rates updated.");
      onOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not update the job rates."));
    } finally {
      setSaving(false);
    }
  };

  const movement = preview ? preview.proposedValue - preview.currentValue : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Rates for this job</DialogTitle>
          <DialogDescription>
            Choose the rate card this job is charged on, and whether its rates
            are frozen as at a date.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Rate card</Label>
            <Select value={cardId} onValueChange={setCardId}>
              <SelectTrigger aria-label="Rate card">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={STANDARD}>
                  Standard (your grade rates)
                </SelectItem>
                {cardOptions.map((c) => (
                  <SelectItem key={c._id} value={c._id as string}>
                    {c.name} · {describeMultiplier(c.multiplier)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Switch
                id="job-rates-frozen"
                checked={frozen}
                onCheckedChange={setFrozen}
              />
              <Label htmlFor="job-rates-frozen">
                Freeze rates as at a date
              </Label>
            </div>
            {frozen ? (
              <>
                <DatePicker
                  value={ratesAsAt}
                  onChange={(isoDate) => setRatesAsAt(isoDate ?? "")}
                  placeholder="Rates as at…"
                />
                <p className="text-xs text-muted-foreground">
                  All time on this job is charged at the rates that applied on
                  this date, whenever the work is done. Later rate rises do not
                  reach it. For example, choose 30 June 2024 to keep the job on
                  FY24 rates.
                </p>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">
                Off: each entry is charged at the rate in force on the day the
                work was done.
              </p>
            )}
          </div>

          {changed && !freezeIncomplete && (
            <div
              role="status"
              className="space-y-1.5 rounded-md bg-amber-50 px-3 py-2.5 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
            >
              {previewFailed ? (
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs">
                    Could not work out what this changes, so it cannot be
                    applied yet.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 shrink-0 text-xs"
                    onClick={() => void retryPreview()}
                  >
                    Try again
                  </Button>
                </div>
              ) : preview === undefined ? (
                <p className="flex items-center gap-2 text-xs">
                  <Loader2 className="size-3.5 animate-spin" />
                  Working out what this changes…
                </p>
              ) : (
                <>
                  <p className="font-medium">
                    {preview.entryCount === 0
                      ? "No unapproved time on this job yet."
                      : `${preview.entryCount === 1 ? "1 unapproved entry is" : `${preview.entryCount} unapproved entries are`} re-valued: ${formatAud(preview.currentValue)} to ${formatAud(preview.proposedValue)} (${movement >= 0 ? "+" : "-"}${formatAud(Math.abs(movement))}).`}
                  </p>
                  <p className="text-xs">
                    {preview.approvedCount === 0
                      ? "There is no approved time on this job."
                      : `${preview.approvedCount === 1 ? "1 approved entry keeps" : `${preview.approvedCount} approved entries keep`} the rate ${preview.approvedCount === 1 ? "it was" : "they were"} approved at.`}{" "}
                    New time is charged on the new terms from now on.
                  </p>
                  {preview.unratedCount > 0 && (
                    <p className="text-xs font-medium">
                      {preview.unratedCount === 1
                        ? "1 entry"
                        : `${preview.unratedCount} entries`}{" "}
                      would have no rate at all, because{" "}
                      {preview.unratedPeople.join(", ")} had no rate on that
                      date. Give them an individual rate, or pick a later date.
                    </p>
                  )}
                  {preview.partial && (
                    <p className="text-xs">
                      This job has more time than the preview reads, so these
                      figures are a minimum.
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {current.history.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">
                Change history
              </p>
              <ul className="space-y-0.5 text-xs text-muted-foreground">
                {(showAllHistory
                  ? current.history
                  : current.history.slice(0, RECENT_HISTORY)
                ).map((h) => (
                  <li key={h.timestamp}>
                    {formatIsoDate(
                      new Date(h.timestamp).toISOString().slice(0, 10),
                    )}{" "}
                    · {h.actor} · {h.reason}
                  </li>
                ))}
              </ul>
              {current.history.length > RECENT_HISTORY && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="-ml-2 h-7 text-xs text-muted-foreground"
                  onClick={() => setShowAllHistory((prev) => !prev)}
                >
                  {showAllHistory
                    ? "Show fewer"
                    : `Show all ${current.history.length} changes`}
                </Button>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!canApply} onClick={() => void handleApply()}>
            {saving && <Loader2 className="size-4 animate-spin" />}
            Apply to this job
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
